import { useEffect, useMemo, useState } from "react";
import { getToken } from "../utils/auth";

const API_URL =
  process.env.REACT_APP_API_URL || "https://app.nvplaydigital.com";

function getUsuarioDoToken() {
  try {
    const token = getToken();
    if (!token) return null;

    const partes = token.split(".");
    if (partes.length < 2) return null;

    return JSON.parse(atob(partes[1]));
  } catch {
    return null;
  }
}

function formatarNumero(valor) {
  return Number(valor || 0).toLocaleString("pt-BR");
}

function formatarPercentual(valor) {
  return `${Number(valor || 0).toFixed(1)}%`;
}

function formatarData(data) {
  if (!data) return "-";

  try {
    return new Date(data).toLocaleString("pt-BR");
  } catch {
    return "-";
  }
}

export default function ControleCreditos() {
  const usuario = useMemo(() => getUsuarioDoToken(), []);
  const role = usuario?.role || "";

  const podeAcessar = ["master", "gestor", "submaster", "franquia"].includes(role);
  const podeGerenciar = ["master", "gestor", "submaster"].includes(role);
  const isFranquia = role === "franquia";

  const [aba, setAba] = useState("visao");
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [resumo, setResumo] = useState(null);
  const [servidores, setServidores] = useState([]);
  const [saldos, setSaldos] = useState([]);
  const [historico, setHistorico] = useState([]);
  const [destinos, setDestinos] = useState([]);

  const [servidorForm, setServidorForm] = useState({
    id: "",
    nome: "",
    observacao: "",
  });

  const [transferencia, setTransferencia] = useState({
    servidorId: "",
    destinoId: "",
    quantidade: "",
    observacao: "",
  });

  async function api(path, options = {}) {
    const token = getToken();

    const resposta = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
        ...(options.headers || {}),
      },
    });

    const data = await resposta.json().catch(() => ({}));

    if (!resposta.ok) {
      throw new Error(data.error || "Erro ao comunicar com o servidor.");
    }

    return data;
  }

  async function carregarTudo() {
    if (!podeAcessar) return;

    setCarregando(true);
    setErro("");

    try {
      const [resumoData, servidoresData, saldosData, historicoData] =
        await Promise.all([
          api("/api/creditos/resumo"),
          api("/api/creditos/servidores"),
          api("/api/creditos/saldos"),
          api("/api/creditos/historico"),
        ]);

      setResumo(resumoData || null);
      setServidores(Array.isArray(servidoresData) ? servidoresData : servidoresData.servidores || []);
      setSaldos(Array.isArray(saldosData) ? saldosData : saldosData.saldos || []);
      setHistorico(Array.isArray(historicoData) ? historicoData : historicoData.historico || []);
      setDestinos(Array.isArray(saldosData?.destinos) ? saldosData.destinos : []);
    } catch (err) {
      setErro(err.message || "Erro ao carregar Controle de Créditos.");
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => {
    carregarTudo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function salvarServidor(e) {
    e.preventDefault();

    setErro("");
    setSucesso("");

    if (!servidorForm.nome.trim()) {
      setErro("Informe o nome do servidor.");
      return;
    }

    try {
      if (servidorForm.id) {
        await api(`/api/creditos/servidores/${servidorForm.id}`, {
          method: "PUT",
          body: JSON.stringify({
            nome: servidorForm.nome,
            observacao: servidorForm.observacao,
          }),
        });

        setSucesso("Servidor atualizado com sucesso.");
      } else {
        await api("/api/creditos/servidores", {
          method: "POST",
          body: JSON.stringify({
            nome: servidorForm.nome,
            observacao: servidorForm.observacao,
          }),
        });

        setSucesso("Servidor cadastrado com sucesso.");
      }

      setServidorForm({ id: "", nome: "", observacao: "" });
      await carregarTudo();
    } catch (err) {
      setErro(err.message || "Erro ao salvar servidor.");
    }
  }

  function editarServidor(servidor) {
    setServidorForm({
      id: servidor._id || servidor.id,
      nome: servidor.nome || "",
      observacao: servidor.observacao || "",
    });

    setAba("servidores");
  }

  async function inativarServidor(servidor) {
    const id = servidor._id || servidor.id;

    if (!id) return;

    const confirmar = window.confirm(
      "Deseja inativar este servidor? Ele sairá das novas transferências, mas continuará no histórico."
    );

    if (!confirmar) return;

    setErro("");
    setSucesso("");

    try {
      await api(`/api/creditos/servidores/${id}/inativar`, {
        method: "PATCH",
      });

      setSucesso("Servidor inativado com sucesso.");
      await carregarTudo();
    } catch (err) {
      setErro(err.message || "Erro ao inativar servidor.");
    }
  }

  async function transferirCreditos(e) {
    e.preventDefault();

    setErro("");
    setSucesso("");

    const quantidade = Number(transferencia.quantidade || 0);

    if (!transferencia.servidorId) {
      setErro("Selecione um servidor.");
      return;
    }

    if (!transferencia.destinoId) {
      setErro("Selecione o destino.");
      return;
    }

    if (!quantidade || quantidade <= 0) {
      setErro("A quantidade precisa ser maior que zero.");
      return;
    }

    try {
      await api("/api/creditos/transferir", {
        method: "POST",
        body: JSON.stringify({
          servidorId: transferencia.servidorId,
          destinoId: transferencia.destinoId,
          quantidade,
          observacao: transferencia.observacao,
        }),
      });

      setSucesso("Lançamento manual de créditos registrado com sucesso.");
      setTransferencia({
        servidorId: "",
        destinoId: "",
        quantidade: "",
        observacao: "",
      });

      await carregarTudo();
    } catch (err) {
      setErro(err.message || "Erro ao transferir créditos.");
    }
  }

  const servidoresAtivos = servidores.filter((s) => s.ativo !== false);

  const cardsFranquia = [
    {
      titulo: "Créditos Recebidos",
      valor: formatarNumero(resumo?.recebidos),
    },
    {
      titulo: "Créditos Utilizados",
      valor: formatarNumero(resumo?.utilizados),
    },
    {
      titulo: "Saldo Disponível",
      valor: formatarNumero(resumo?.disponiveis),
    },
    {
      titulo: "Utilização %",
      valor: formatarPercentual(resumo?.utilizacao),
    },
  ];

  const cardsGestor = [
    {
      titulo: "Total Distribuído",
      valor: formatarNumero(resumo?.totalDistribuido),
    },
    {
      titulo: "Total Utilizado",
      valor: formatarNumero(resumo?.totalUtilizado),
    },
    {
      titulo: "Saldo da Rede",
      valor: formatarNumero(resumo?.saldoRede),
    },
    {
      titulo: "Franquias com Crédito",
      valor: formatarNumero(resumo?.franquiasComCredito),
    },
  ];

  const cards = isFranquia ? cardsFranquia : cardsGestor;

  if (!podeAcessar) {
    return (
      <>
        <style>{css}</style>
        <div className="creditosPage">
          <div className="bloqueado">
            <h1>Acesso indisponível</h1>
            <p>Este módulo não está disponível para colaboradores.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>

      <div className="creditosPage">
        <div className="header">
          <div>
            <span className="tag">V1 isolada</span>
            <h1>📦 Controle de Créditos</h1>
            <p>
              Cadastre servidores, distribua créditos e acompanhe saldos e histórico da rede.
            </p>
          </div>

          <button className="refreshBtn" onClick={carregarTudo} disabled={carregando}>
            {carregando ? "Carregando..." : "Atualizar"}
          </button>
        </div>

        {erro && <div className="alert erro">{erro}</div>}
        {sucesso && <div className="alert sucesso">{sucesso}</div>}

        <section className="cards">
          {cards.map((card) => (
            <div className="card" key={card.titulo}>
              <span>{card.titulo}</span>
              <strong>{card.valor}</strong>
            </div>
          ))}
        </section>

        <div className="tabs">
          <button className={aba === "visao" ? "tab active" : "tab"} onClick={() => setAba("visao")}>
            Visão Geral
          </button>

          {podeGerenciar && (
            <button className={aba === "servidores" ? "tab active" : "tab"} onClick={() => setAba("servidores")}>
              Servidores
            </button>
          )}

          {podeGerenciar && (
            <button className={aba === "transferir" ? "tab active" : "tab"} onClick={() => setAba("transferir")}>
              Lançar créditos
            </button>
          )}

          <button className={aba === "saldos" ? "tab active" : "tab"} onClick={() => setAba("saldos")}>
            Saldos
          </button>

          <button className={aba === "historico" ? "tab active" : "tab"} onClick={() => setAba("historico")}>
            Histórico
          </button>
        </div>

        {aba === "visao" && (
          <section className="panel">
            <h2>Resumo do módulo</h2>

            <div className="grid2">
              <div className="infoBox">
                <h3>Servidores ativos</h3>
                <strong>{formatarNumero(servidoresAtivos.length)}</strong>
                <p>Disponíveis para novas transferências.</p>
              </div>

              <div className="infoBox">
                <h3>Movimentações</h3>
                <strong>{formatarNumero(historico.length)}</strong>
                <p>Histórico preservado para conferência.</p>
              </div>
            </div>
          </section>
        )}

        {aba === "servidores" && podeGerenciar && (
          <section className="panel">
            <h2>Servidores</h2>

            <form className="form" onSubmit={salvarServidor}>
              <label>
                Nome do servidor
                <input
                  value={servidorForm.nome}
                  onChange={(e) => setServidorForm({ ...servidorForm, nome: e.target.value })}
                  placeholder="Ex: Servidor A"
                />
              </label>

              <label>
                Observação opcional
                <input
                  value={servidorForm.observacao}
                  onChange={(e) => setServidorForm({ ...servidorForm, observacao: e.target.value })}
                  placeholder="Ex: Servidor principal da rede"
                />
              </label>

              <div className="actions">
                <button className="primary" type="submit">
                  {servidorForm.id ? "Salvar Alterações" : "Cadastrar Servidor"}
                </button>

                {servidorForm.id && (
                  <button
                    className="secondary"
                    type="button"
                    onClick={() => setServidorForm({ id: "", nome: "", observacao: "" })}
                  >
                    Cancelar edição
                  </button>
                )}
              </div>
            </form>

            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Servidor</th>
                    <th>Observação</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>

                <tbody>
                  {servidores.length === 0 && (
                    <tr>
                      <td colSpan="4">Nenhum servidor cadastrado.</td>
                    </tr>
                  )}

                  {servidores.map((servidor) => (
                    <tr key={servidor._id || servidor.id}>
                      <td>{servidor.nome}</td>
                      <td>{servidor.observacao || "-"}</td>
                      <td>
                        <span className={servidor.ativo === false ? "status off" : "status on"}>
                          {servidor.ativo === false ? "Inativo" : "Ativo"}
                        </span>
                      </td>
                      <td>
                        <div className="tableActions">
                          <button onClick={() => editarServidor(servidor)}>Editar</button>
                          {servidor.ativo !== false && (
                            <button className="danger" onClick={() => inativarServidor(servidor)}>
                              Inativar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {aba === "transferir" && podeGerenciar && (
          <section className="panel">
            <h2>Lançamento manual de créditos</h2>
            <p className="helperText">
              Registre aqui os créditos já liberados manualmente no painel externo. Este lançamento não acessa nem altera o Sigma.
            </p>

            <form className="form" onSubmit={transferirCreditos}>
              <label>
                Servidor
                <select
                  value={transferencia.servidorId}
                  onChange={(e) => setTransferencia({ ...transferencia, servidorId: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {servidoresAtivos.map((servidor) => (
                    <option key={servidor._id || servidor.id} value={servidor._id || servidor.id}>
                      {servidor.nome}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Destino
                <select
                  value={transferencia.destinoId}
                  onChange={(e) => setTransferencia({ ...transferencia, destinoId: e.target.value })}
                >
                  <option value="">Selecione</option>
                  {destinos.map((destino) => (
                    <option key={destino._id || destino.id} value={destino._id || destino.id}>
                      {destino.nomeEmpresa || destino.email}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Quantidade
                <input
                  type="number"
                  min="1"
                  value={transferencia.quantidade}
                  onChange={(e) => setTransferencia({ ...transferencia, quantidade: e.target.value })}
                  placeholder="Ex: 100"
                />
              </label>

              <label>
                Observação
                <input
                  value={transferencia.observacao}
                  onChange={(e) => setTransferencia({ ...transferencia, observacao: e.target.value })}
                  placeholder="Ex: Campanha Junho"
                />
              </label>

              <button className="primary" type="submit">
                REGISTRAR LANÇAMENTO
              </button>
            </form>
          </section>
        )}

        {aba === "saldos" && (
          <section className="panel">
            <h2>Saldos por usuário/franquia</h2>

            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Franquia/Usuário</th>
                    <th>Servidor</th>
                    <th>Recebidos</th>
                    <th>Utilizados</th>
                    <th>Disponíveis</th>
                    <th>Utilização</th>
                  </tr>
                </thead>

                <tbody>
                  {saldos.length === 0 && (
                    <tr>
                      <td colSpan="6">Nenhum saldo encontrado.</td>
                    </tr>
                  )}

                  {saldos.map((saldo) => (
                    <tr key={saldo._id || `${saldo.usuarioId}-${saldo.servidorId}`}>
                      <td>{saldo.usuarioNome || saldo.nomeEmpresa || saldo.usuario || "-"}</td>
                      <td>{saldo.servidorNome || "-"}</td>
                      <td>{formatarNumero(saldo.recebidos)}</td>
                      <td>{formatarNumero(saldo.utilizados)}</td>
                      <td>{formatarNumero(saldo.disponiveis)}</td>
                      <td>{formatarPercentual(saldo.utilizacao)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {aba === "historico" && (
          <section className="panel">
            <h2>Histórico de movimentações</h2>

            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Origem</th>
                    <th>Destino</th>
                    <th>Servidor</th>
                    <th>Quantidade</th>
                    <th>Tipo</th>
                    <th>Registro</th>
                    <th>Observação</th>
                  </tr>
                </thead>

                <tbody>
                  {historico.length === 0 && (
                    <tr>
                      <td colSpan="8">Nenhuma movimentação encontrada.</td>
                    </tr>
                  )}

                  {historico.map((mov) => (
                    <tr key={mov._id || mov.id}>
                      <td>{formatarData(mov.data)}</td>
                      <td>{mov.origemNome || "-"}</td>
                      <td>{mov.destinoNome || "-"}</td>
                      <td>{mov.servidorNome || "-"}</td>
                      <td>{formatarNumero(mov.quantidade)}</td>
                      <td>{mov.tipo === "TRANSFERENCIA" ? "Distribuição" : mov.tipo || "-"}</td>
                      <td>{mov.origemLancamento === "automatica" ? "Automático" : "Manual"}</td>
                      <td>{mov.observacao || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </>
  );
}

const css = `
.creditosPage {
  min-height: calc(100vh - 90px);
  color: #e5e7eb;
}

.header {
  background: linear-gradient(135deg, #020617, #0f172a, #1e293b);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 20px;
  padding: 22px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.22);
}

.helperText {
  margin: -4px 0 18px;
  color: #94a3b8;
  line-height: 1.5;
}

.header h1 {
  margin: 8px 0 6px;
  font-size: clamp(24px, 4vw, 34px);
  color: #ffffff;
}

.header p {
  margin: 0;
  color: #cbd5e1;
  max-width: 720px;
  line-height: 1.5;
}

.tag {
  display: inline-flex;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(34, 197, 94, 0.12);
  color: #86efac;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.refreshBtn,
.primary,
.secondary,
.tableActions button {
  border: none;
  border-radius: 12px;
  padding: 11px 14px;
  font-weight: 800;
  cursor: pointer;
}

.refreshBtn {
  background: #ffffff;
  color: #0f172a;
}

.refreshBtn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.alert {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  font-weight: 700;
}

.alert.erro {
  background: #fee2e2;
  color: #991b1b;
}

.alert.sucesso {
  background: #dcfce7;
  color: #166534;
}

.cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 16px;
}

.card {
  background: linear-gradient(135deg, #0f172a, #111827);
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 18px;
  padding: 18px;
  min-height: 115px;
  box-shadow: 0 16px 35px rgba(15, 23, 42, 0.16);
}

.card span {
  color: #94a3b8;
  display: block;
  margin-bottom: 14px;
  font-weight: 700;
}

.card strong {
  color: #ffffff;
  font-size: clamp(24px, 4vw, 34px);
}

.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 18px 0;
}

.tab {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 10px 14px;
  cursor: pointer;
  font-weight: 800;
}

.tab.active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.panel {
  background: #ffffff;
  color: #0f172a;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.12);
  border: 1px solid #e2e8f0;
}

.panel h2 {
  margin: 0 0 16px;
  color: #0f172a;
}

.grid2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.infoBox {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px;
}

.infoBox h3 {
  margin: 0 0 10px;
}

.infoBox strong {
  font-size: 30px;
}

.infoBox p {
  color: #64748b;
}

.form {
  display: grid;
  gap: 14px;
  max-width: 760px;
}

.form label {
  display: grid;
  gap: 7px;
  font-weight: 800;
}

.form input,
.form select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  padding: 12px;
  font-size: 15px;
  outline: none;
}

.form input:focus,
.form select:focus {
  border-color: #0f172a;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.12);
}

.actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.primary {
  background: #0f172a;
  color: #ffffff;
}

.secondary {
  background: #e2e8f0;
  color: #0f172a;
}

.tableWrap {
  overflow-x: auto;
  margin-top: 18px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

th {
  background: #0f172a;
  color: #ffffff;
  text-align: left;
  padding: 13px;
  font-size: 13px;
}

td {
  padding: 13px;
  border-bottom: 1px solid #e2e8f0;
  color: #0f172a;
  vertical-align: middle;
}

tr:last-child td {
  border-bottom: none;
}

.status {
  display: inline-flex;
  padding: 6px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 900;
}

.status.on {
  background: #dcfce7;
  color: #166534;
}

.status.off {
  background: #fee2e2;
  color: #991b1b;
}

.tableActions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tableActions button {
  background: #e2e8f0;
  color: #0f172a;
  padding: 8px 10px;
}

.tableActions .danger {
  background: #fee2e2;
  color: #991b1b;
}

.bloqueado {
  background: #ffffff;
  color: #0f172a;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #e2e8f0;
}

@media (max-width: 980px) {
  .cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .header {
    flex-direction: column;
  }
}

@media (max-width: 560px) {
  .cards,
  .grid2 {
    grid-template-columns: 1fr;
  }

  .panel,
  .header {
    border-radius: 16px;
    padding: 15px;
  }

  .tab {
    flex: 1;
    min-width: 130px;
  }
}
`;
